import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useMapStore } from '../../store/mapStore';
import {
  searchUnsplashPhotos,
  getLocationSearchQuery,
  getOptimizedImageUrl,
  getUnsplashAttribution,
  type UnsplashImage
} from '../../utils/unsplash';

const LocationModal = () => {
  const { selectedLocation, setSelectedLocation } = useMapStore();
  const [images, setImages] = useState<UnsplashImage[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoadingImages, setIsLoadingImages] = useState(true);

  // Fetch Unsplash images when location changes
  useEffect(() => {
    if (!selectedLocation) return;

    const fetchImages = async () => {
      setIsLoadingImages(true);
      const query = getLocationSearchQuery(selectedLocation.id);
      const results = await searchUnsplashPhotos(query, 3);
      setImages(results);
      setCurrentImageIndex(0);
      setIsLoadingImages(false);
    };

    fetchImages();
  }, [selectedLocation?.id]);

  if (!selectedLocation) return null;

  const currentImage = images[currentImageIndex];
  const hasImages = images.length > 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[2000] flex items-center justify-center modal-overlay pointer-events-none"
        onClick={() => setSelectedLocation(null)}
      >
        <motion.div
          initial={{ scale: 0.9, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 50 }}
          className="relative w-11/12 h-5/6 bg-parchment-base rounded-lg shadow-2xl flex overflow-hidden pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={() => setSelectedLocation(null)}
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-copper text-parchment-base rounded-full hover:bg-gold transition-colors"
            aria-label="Close modal"
          >
            ✕
          </button>

          {/* Left Panel - Images */}
          <div className="w-1/2 bg-parchment-dark p-8 flex flex-col">
            <div className="flex-1 flex items-center justify-center bg-ink-secondary/10 rounded-lg relative overflow-hidden">
              {isLoadingImages ? (
                <div className="text-center text-ink-secondary p-8">
                  <div className="animate-spin text-4xl mb-4">⏳</div>
                  <p className="font-ui text-lg">Loading images...</p>
                </div>
              ) : hasImages && currentImage ? (
                <>
                  <img
                    src={getOptimizedImageUrl(currentImage, 800, 600)}
                    alt={currentImage.alt_description || selectedLocation.name}
                    className="w-full h-full object-cover rounded"
                  />
                  {/* Image attribution */}
                  <div
                    className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded"
                    dangerouslySetInnerHTML={{ __html: getUnsplashAttribution(currentImage) }}
                  />
                </>
              ) : (
                <div className="text-center text-ink-secondary p-8">
                  <div className="text-6xl mb-4">🗺️</div>
                  <p className="font-display text-xl">Ancient Location</p>
                  <p className="text-sm mt-2">No images available</p>
                </div>
              )}
            </div>

            {/* Image Navigation */}
            {hasImages && images.length > 1 && (
              <div className="flex justify-center items-center gap-4 mt-4">
                <button
                  onClick={() => setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))}
                  className="w-8 h-8 flex items-center justify-center bg-copper text-parchment-base rounded-full hover:bg-gold transition-colors"
                  aria-label="Previous image"
                >
                  ←
                </button>
                <div className="flex gap-2">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentImageIndex ? 'bg-copper' : 'bg-ink-secondary/30'
                      }`}
                      aria-label={`View image ${index + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={() => setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))}
                  className="w-8 h-8 flex items-center justify-center bg-copper text-parchment-base rounded-full hover:bg-gold transition-colors"
                  aria-label="Next image"
                >
                  →
                </button>
              </div>
            )}
          </div>

          {/* Right Panel - Content */}
          <div className="w-1/2 p-8 overflow-y-auto">
            {/* Breadcrumb */}
            <div className="text-sm text-copper font-ui mb-4">
              {selectedLocation.book} // {selectedLocation.modernName.split(',')[1] || selectedLocation.modernName.split(',')[0]}
            </div>

            {/* Title */}
            <h2 className="font-display text-4xl text-ink-primary mb-2">
              {selectedLocation.name}
            </h2>

            {/* Modern Name */}
            <p className="text-xl text-ink-secondary mb-2">{selectedLocation.modernName}</p>

            {/* Coordinates */}
            <p className="text-sm text-ink-secondary/70 font-mono mb-6">
              {selectedLocation.coordinates.lat.toFixed(4)}°N, {selectedLocation.coordinates.lng.toFixed(4)}°E
            </p>

            {/* Film Badge */}
            {selectedLocation.nolansFilmLocation && (
              <div className="film-badge inline-block mb-6">
                🎬 Featured in Nolan's Film
              </div>
            )}

            {/* Tabs/Sections */}
            <div className="space-y-6">
              {/* The Myth */}
              <div>
                <h3 className="font-display text-2xl text-copper mb-3">The Myth</h3>
                <p className="font-body text-lg text-ink-primary leading-relaxed">
                  {selectedLocation.mythStory}
                </p>
              </div>

              {/* Modern Day */}
              <div>
                <h3 className="font-display text-2xl text-copper mb-3">Modern Day</h3>
                <p className="font-body text-lg text-ink-primary leading-relaxed">
                  {selectedLocation.modernInfo}
                </p>
              </div>

              {/* Things to Do */}
              {selectedLocation.thingsToDo && selectedLocation.thingsToDo.length > 0 && (
                <div>
                  <h3 className="font-display text-2xl text-copper mb-3">Things to Do</h3>
                  <div className="space-y-3">
                    {selectedLocation.thingsToDo.map((thing, index) => (
                      <div key={index} className="parchment-card p-4">
                        <h4 className="font-ui text-lg text-ink-primary font-bold">
                          {thing.name}
                        </h4>
                        <p className="text-sm text-copper uppercase tracking-wide">
                          {thing.type}
                        </p>
                        <p className="text-ink-secondary mt-1">{thing.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Journey Info */}
              <div className="border-t-2 border-copper pt-4">
                <p className="text-sm text-ink-secondary">
                  <span className="font-bold">Journey Day:</span> {selectedLocation.journeyDay}{' '}
                  <span className="ml-4 font-bold">Importance:</span>{' '}
                  <span className="capitalize">{selectedLocation.importance}</span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LocationModal;
